import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MapComponent from "../components/MapComponent";
import avatarImage from "../assets/icons/user-avatar.png";
import { deleteFarm, getUserDetail, getUserPoints } from "../services/farmers";
import { toast } from "react-toastify";
import UserOrderHistory from "./sales/UserOrderHistory";
import StyledHeadingText from "../components/Fonts/StyledHeadingText";
import Button from "../components/buttons/Button";
import ShowModal from "../utils/ShowModal";
import ExpenseForm from "./ExpenseForm";
import CommodityForm from "./CommodityForm";
import { postNewSatelliteFarm } from "../services/satelliteFarm";
import { Form, Formik } from "formik";
import AsyncFormikDropdown from "../components/inputFields/AsyncFormikDropDown";
import { getFpos, patchUserOrganization, postUserOrganization } from "../services/fpo";
import { Delete } from "lucide-react";
import { confirmDanger } from "../utils/confirmDialog";
import ExpenseCard from "../components/ExpenseCard";
import ExpenseDetailedView from "../components/ExpenseDetailedView";
import { getUserRole } from "../helpers/local_storage_helpers";

// Main Component
function UserDashboardView({ item, onUpdateAny }) {
    const navigate = useNavigate();
    const [pointsData, setPointsData] = useState([]);
    const [pointsAnalytics, setPointsAnalytics] = useState({
        total_points: 0,
        total_used_points: 0,
        total_remaining_points: 0
    });
    const [farmsData, setFarmsData] = useState([]);
    const [satelliteFarmsData, setSatelliteFarmsData] = useState([]);
    const [purchaseHistory, setPurchaseHistory] = useState([]);
    const [expensesData, setExpensesData] = useState([]);
    const [forumStats, setForumStats] = useState({});
    const [devicesData, setDevicesData] = useState([]);

    const [farmAreas, setFarmAreas] = useState([]);
    const [mapLocation, setMapLocation] = useState([22.7196, 75.8577]);
    const [profitLossData, setProfitLossData] = useState(0);
    const [registeredDevices, setRegisteredDevices] = useState(0);
    const [showExpenseAdd, setShowExpenseAdd] = useState(false);
    const [showMandiAdd, setShowMandiAdd] = useState(false);
    const [showFpoAdd, setShowFpoAdd] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [isExpenseDetailOpen, setIsExpenseDetailOpen] = useState(false);

    const handleExpenseClick = (expense) => {
        setSelectedExpense(expense);
        setIsExpenseDetailOpen(true);
    };

    const handleCloseExpenseDetail = () => {
        setIsExpenseDetailOpen(false);
        setSelectedExpense(null);
    };

    const fetchPoints = async () => {
        try {
            let res = await getUserPoints({
                userId: item.id
            });
            if (res.status === 200) {
                const data = res.data;
                // Handle new API response structure
                if (data.analytic_data && data.history_data) {
                    setPointsAnalytics({
                        total_points: data.analytic_data.total_points || 0,
                        total_used_points: data.analytic_data.total_used_points || 0,
                        total_remaining_points: data.analytic_data.total_remaining_points || 0
                    });
                    setPointsData(data.history_data || []);
                } else {
                    // Fallback for old structure
                    setPointsData(Array.isArray(data) ? data : []);
                    setPointsAnalytics({
                        total_points: Array.isArray(data) ? data.reduce((acc, point) => acc + (point.points || 0), 0) : 0,
                        total_used_points: 0,
                        total_remaining_points: Array.isArray(data) ? data.reduce((acc, point) => acc + (point.points || 0), 0) : 0
                    });
                }
            } else {
                toast.error("Unable to fetch Points");
            }
        } catch (error) {
            console.error("Error:", error);
        }
    };

    const fetchHistory = async () => {
        try {
            let res = await getUserDetail({
                userId: item.id,
                endPoint: "/history"
            });
            if (res.status === 200) {
                const data = res.data;
                setPurchaseHistory(data);
            } else {
                toast.error("Unable to fetch History");
            }
        } catch (error) {
            console.error("Error:", error);
        }
    };

    const fetchExpenses = async () => {
        try {
            let res = await getUserDetail({
                userId: item.id,
                endPoint: "/expense"
            });
            if (res.status === 200) {
                const data = res.data;
                setExpensesData(data);
                setProfitLossData(res.data.filter(e => parseInt(e.difference_updated.replace(',', '')) < 0))
            } else {
                toast.error("Unable to fetch Expenses");
            }
        } catch (error) {
            console.error("Error:", error);
        }
    };

    const fetchDevices = async () => {
        try {
            let res = await getUserDetail({
                userId: item.id,
                endPoint: "/device"
            });
            if (res.status === 200) {
                const data = res.data;
                setDevicesData(data);
                setRegisteredDevices(data.filter(d => d.is_registered).length)
            } else {
                toast.error("Unable to fetch Expenses");
            }
        } catch (error) {
            console.error("Error:", error);
        }
    };

    const fetchFarms = async () => {
        try {
            let res = await getUserDetail({
                userId: item.id,
                endPoint: "/farms"
            });
            if (res.status === 200) {
                const data = res.data;
                setFarmsData(data);
                if (JSON.parse(data[0].geo_tag).length > 0) {
                    setMapLocation([
                        JSON.parse(data[0].geo_tag)[0].lat,
                        JSON.parse(data[0].geo_tag)[0].lng,
                    ]);
                } else {
                    setMapLocation([
                        22.7196,
                        75.8577,
                    ]);
                }
                setFarmAreas(data.map(farm => ({
                    area: JSON.parse(farm.geo_tag),
                    title: `${farm.name} (${farm.size} ${farm.area_unit})`,
                })));
            } else {
                toast.error("Unable to fetch Farms");
            }
        } catch (error) {
            console.error("Error:", error);
        }
    };

    const changeMapLocation = async (latLon) => {
        const object = JSON.parse(latLon);
        setMapLocation([
            object[0].lat,
            object[0].lng,
        ]);
    }

    const fetchSatelliteFarms = async () => {
        try {
            let res = await getUserDetail({
                userId: item.id,
                endPoint: "/satfarms"
            });
            if (res.status === 200) {
                const data = res.data;
                setSatelliteFarmsData(data);
            } else {
                toast.error("Unable to fetch Farms");
            }
        } catch (error) {
            console.error("Error:", error);
        }
    };

    useEffect(() => {
        fetchPoints();
        fetchHistory();
        fetchExpenses();
        fetchFarms();
        fetchSatelliteFarms();
        fetchDevices();
    }, []);

    const closeExpenseModal = () => {
        setShowExpenseAdd(false);
    };

    const openExpenseModal = () => {
        setShowExpenseAdd(true);
    };

    const closeMandiModal = () => {
        setShowMandiAdd(false);
    };

    const openMandiModal = () => {
        setShowMandiAdd(true);
    };

    const handleExpenseSave = () => {
        setShowExpenseAdd(false);
        fetchExpenses();
    };

    const handleSatelliteFarm = async (farm) => {
        try {
            const data = {
                subscription_start_date: new Date().toISOString(),
                subscription_end_date: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString(),
                sales_team_approval: "approved",
                farm: farm.id,
            };
            const response = await postNewSatelliteFarm(data);
            if (response.status === 201) {
                toast.success("Satellite farm upgraded successfully");
                fetchSatelliteFarms();
                fetchFarms();
            } else {
                toast.error("Unable to upgrade to satellite farm");
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error("Unable to upgrade to satellite farm");
        }
    };

    const toggleFpoAddModal = () => {
        setShowFpoAdd(!showFpoAdd);
    };

    const closeFpoAddModal = () => {
        setShowFpoAdd(false);
    };

    const handleFpoAdd = async (values) => {
        try {
            const response = item.user_organization_id ? await patchUserOrganization({ data: { fpo: values.fpo, user: item.id }, id: item.user_organization_id }) : await postUserOrganization({ data: { fpo: values.fpo, user: item.id } });
            if (response.status === 201 || response.status === 200) {
                toast.success("FPO added successfully");
                onUpdateAny();
                closeFpoAddModal();
            } else {
                toast.error("Unable to add FPO");
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error("Unable to add FPO");
        }
    };

    const handleDeleteFarm = async (farm) => {
        confirmDanger("Are you sure, you want to delete this farm?", "Delete Farm").then(async (result) => {
            if (result) {
                const response = await deleteFarm(farm.id);
                if (response.status === 204) {
                    toast.success("Farm deleted successfully");
                    fetchFarms();
                } else {
                    toast.error("Unable to delete farm");
                }
            }
        });
    };

    return (
        <div className="p-3 space-y-3 bg-gradient-to-br from-gray-50 to-blue-50/30 min-h-screen max-w-7xl mx-auto">
            {/* Enhanced Header Section */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200/50 p-4">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3">
                    <div className="flex items-center gap-4 flex-1">
                        <div className="relative">
                            <div className="bg-gradient-to-br from-blue-100 to-indigo-200 w-16 h-16 rounded-2xl flex items-center justify-center overflow-hidden shadow-sm">
                                <img src={avatarImage} alt="Avatar" className="w-12 h-12" />
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white"></div>
                        </div>
                        <div className="flex-1">
                            <h1 className="text-xl font-bold text-gray-900 mb-1">{item.first_name} {item.last_name}</h1>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                                <div className="flex items-center gap-1">
                                    <span className="text-blue-500">📱</span>
                                    <span>{item.mobile_number}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <span className="text-green-500">📍</span>
                                    <span className="truncate max-w-48">{item.address}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <span className="text-purple-500">📅</span>
                                    <span>Since {new Date(item.registration_date).toLocaleDateString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                        {/* Points Cards - Compact Grid */}
                        <div className="grid grid-cols-3 gap-2">
                            {/* Total Points */}
                            <div className="flex items-center gap-1.5 bg-gradient-to-r from-yellow-50 to-orange-50 p-2 rounded-lg border border-yellow-200 shadow-sm min-w-[90px]">
                                <div className="w-6 h-6 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <span className="text-yellow-600 text-xs">🏆</span>
                                </div>
                                <div className="min-w-0">
                                    <div className="text-yellow-800 font-bold text-sm leading-none">{pointsAnalytics.total_points}</div>
                                    <div className="text-[10px] text-yellow-700">Total</div>
                                </div>
                            </div>
                            {/* Used Points */}
                            <div className="flex items-center gap-1.5 bg-gradient-to-r from-red-50 to-pink-50 p-2 rounded-lg border border-red-200 shadow-sm min-w-[90px]">
                                <div className="w-6 h-6 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <span className="text-red-600 text-xs">📉</span>
                                </div>
                                <div className="min-w-0">
                                    <div className="text-red-800 font-bold text-sm leading-none">{pointsAnalytics.total_used_points}</div>
                                    <div className="text-[10px] text-red-700">Used</div>
                                </div>
                            </div>
                            {/* Remaining Points */}
                            <div className="flex items-center gap-1.5 bg-gradient-to-r from-green-50 to-emerald-50 p-2 rounded-lg border border-green-200 shadow-sm min-w-[90px]">
                                <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <span className="text-green-600 text-xs">💰</span>
                                </div>
                                <div className="min-w-0">
                                    <div className="text-green-800 font-bold text-sm leading-none">{pointsAnalytics.total_remaining_points}</div>
                                    <div className="text-[10px] text-green-700">Remaining</div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            <div className="text-sm text-gray-600 font-medium bg-gray-50 px-3 py-1 rounded-full">
                                FPO: {item.fpo || "No FPO"}
                            </div>
                            {
                                getUserRole() === "admin" && (
                                    <div className="flex gap-2">
                                        <Button
                                            children="Add Points"
                                            className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg text-xs font-medium py-2 px-4 hover:shadow-md transition-all duration-200 transform hover:scale-105"
                                            onClick={() => navigate("/farmers/add-points", { state: { userId: item.id } })}
                                        />
                                        <Button
                                            children={item.user_organization_id ? "Edit FPO" : "Add FPO"}
                                            className="bg-gradient-to-r from-primary to-blue-600 text-white rounded-lg text-xs font-medium py-2 px-4 hover:shadow-md transition-all duration-200 transform hover:scale-105"
                                            onClick={toggleFpoAddModal}
                                        />
                                    </div>
                                )
                            }
                        </div>
                    </div>
                </div>

                {/* FPO Add Form */}
                {showFpoAdd && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <h3 className="font-medium text-gray-800 mb-3">{item.user_organization_id ? "Edit FPO" : "Add FPO"}</h3>
                        <Formik
                            initialValues={{ fpo: "" }}
                            onSubmit={handleFpoAdd}
                        >
                            {({ isSubmitting, resetForm }) => (
                                <Form className="flex flex-row gap-3">
                                    <AsyncFormikDropdown
                                        name="fpo"
                                        placeholder="Search FPO"
                                        needSearch={true}
                                        className="flex-1"
                                        apiCallFunction={async (input) => {
                                            const response = await getFpos(`?search=${input}`);
                                            return response.data.results.map((fpo) => ({
                                                value: fpo.id,
                                                label: fpo.name + " | " + fpo.district + " | " + fpo.state,
                                            }));
                                        }}
                                    />
                                    <Button
                                        children={isSubmitting ? "Saving..." : "Save"}
                                        type="submit"
                                        className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
                                        disabled={isSubmitting}
                                    />
                                    <Button
                                        children="Cancel"
                                        type="button"
                                        className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                                        onClick={closeFpoAddModal}
                                    />
                                </Form>
                            )}
                        </Formik>
                    </div>
                )}
            </div>

            {/* Main Content Grid - 2 Columns */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {/* Left Column */}
                <div className="space-y-4">
                    {/* Satellite Monitoring */}
                    <div className="bg-white rounded-xl shadow-md border border-gray-200/50 overflow-hidden">
                        <div className="p-3 border-b bg-gradient-to-r from-blue-50 to-cyan-50">
                            <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                                <span className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center text-sm">🛰️</span>
                                Satellite Monitoring
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full shadow-sm">
                                    {satelliteFarmsData.length} Active
                                </span>
                            </h2>
                        </div>
                        <div className="p-3">
                            {satelliteFarmsData.length > 0 ? (
                                <div className="space-y-2 max-h-[30rem] overflow-y-auto">
                                    {satelliteFarmsData.map((sat) => (
                                        <div key={sat.id} className="border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-all duration-200">
                                            <div className="flex justify-between items-start mb-2">
                                                <h3 className="font-medium text-sm">{sat.farm.name}</h3>
                                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                                    {sat.last_observation}
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-3 gap-1.5">
                                                <HealthIndicator label="NDVI" status={sat.ndvi_status} />
                                                <HealthIndicator label="NDWI" status={sat.ndwi_status} />
                                                <HealthIndicator label="LAI" status={sat.lai_status} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center text-gray-500 py-6 text-sm bg-gray-50 rounded-lg">
                                    <div className="w-12 h-12 bg-gray-200 rounded-full mx-auto mb-2 flex items-center justify-center">
                                        🛰️
                                    </div>
                                    No satellite farms monitored
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Farm Map */}
                    <div className="bg-white rounded-xl shadow-md border border-gray-200/50 overflow-hidden">
                        <div className="p-3 border-b bg-gradient-to-r from-green-50 to-emerald-50">
                            <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                                <span className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center text-sm">🗺️</span>
                                Farm Locations ({farmsData.length} Farms)
                            </h2>
                        </div>
                        <div className="p-3 space-y-3">
                            {/* Map */}
                            <div>
                                <MapComponent
                                    areasList={farmAreas}
                                    boundaryColors={["#2FB95D", "#4A90E2", "#F5A623", "#D0021B"]}
                                    mapLocation={mapLocation}
                                    zoomLevel={16}
                                    height="200px"
                                />
                            </div>
                            {/* Farms List */}
                            <div>
                                <div className="space-y-1.5 max-h-[30rem] overflow-y-auto">
                                    {farmsData.map((farm) => (
                                        <div key={farm.id} className="flex items-center gap-2 p-2 hover:bg-gradient-to-r hover:from-green-50 hover:to-blue-50 rounded-lg cursor-pointer transition-all duration-200" onClick={() => changeMapLocation(farm.geo_tag)}>
                                            <div className="mt-1 flex-shrink-0 w-2 h-2 bg-green-500 rounded-full shadow-sm"></div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-medium text-sm truncate" title={farm.name}>{farm.name}</h3>
                                                <p className="text-xs text-gray-500">
                                                    {farm.size} {farm.area_unit} • {farm.soil_type || 'No soil data'}
                                                </p>
                                            </div>
                                            {!farm.is_satellite_subscribed ? (
                                                <Button
                                                    children="Upgrade"
                                                    className="border-primary border py-0.5 text-primary hover:text-white hover:bg-primary text-xs px-2 rounded-md transition-colors"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSatelliteFarm(farm);
                                                    }}
                                                />
                                            ) : (
                                                <div className="px-2 flex flex-row items-center py-0.5 font-bold bg-gradient-to-r gap-1 from-green-500 to-emerald-500 rounded-full text-xs text-white shadow-sm">
                                                    Satellite
                                                    {
                                                        satelliteFarmsData.find(sat => sat.farm.id === farm.id) && (
                                                            <span className="text-[0.5rem] text-yellow-300">
                                                                (Approved)
                                                            </span>
                                                        )
                                                    }
                                                </div>
                                            )}

                                            {/* add a delete button */}
                                            {
                                                !(satelliteFarmsData.find(sat => sat.farm.id === farm.id)) &&
                                                <Button
                                                    children={<Delete className="text-red-600" size={16} />}
                                                    className="p-0 text-red-500"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteFarm(farm);
                                                    }}
                                                />
                                            }
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                    {/* Financial Overview */}
                    <div className="bg-white rounded-xl shadow-md border border-gray-200/50 overflow-hidden">
                        <div className="p-3 border-b bg-gradient-to-r from-emerald-50 to-green-50">
                            <div className="flex justify-between items-center">
                                <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                                    <span className="w-6 h-6 bg-emerald-100 rounded-lg flex items-center justify-center text-sm">💰</span>
                                    Financial Overview <small className="text-red-600">(Loss Making: {profitLossData.length})</small>
                                </h2>
                                <div className="flex gap-1.5">
                                    <Button
                                        children="+ Commodity"
                                        onClick={openMandiModal}
                                        className="bg-emerald-600 text-white py-1 px-2 text-xs rounded-lg hover:bg-emerald-700 transition-all duration-200 hover:scale-105"
                                    />
                                    <Button
                                        children="+ Expense"
                                        onClick={openExpenseModal}
                                        className="bg-green-600 text-white py-1 px-2 text-xs rounded-lg hover:bg-green-700 transition-all duration-200 hover:scale-105"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="p-3">
                            {expensesData.length > 0 ? (
                                <>
                                    <div className="grid grid-cols-2 gap-2">
                                        {expensesData.slice(0, 6).map((exp) => (
                                            <ExpenseCard
                                                key={exp.id}
                                                expense={{
                                                    id: exp.id,
                                                    type: parseInt(exp.difference_updated.replace(',', '')) >= 0 ? "Income" : "Expense",
                                                    category: exp.commodity_name,
                                                    subType: exp.season,
                                                    amount: Math.abs(parseInt(exp.difference_updated.replace(',', ''))),
                                                    amountUpdated: exp.difference_updated,
                                                    date: exp.created_at || new Date().toISOString(),
                                                    note: exp.description || "No description",
                                                    farm: exp.farm || [],
                                                    commodity_name: exp.commodity_name,
                                                    commodity_image: exp.commodity_image,
                                                    bill: exp.bill || null,
                                                    season: exp.season
                                                }}
                                                onClick={handleExpenseClick}
                                            />
                                        ))}
                                    </div>
                                    {expensesData.length > 6 && (
                                        <div className="text-center text-xs text-gray-500 py-2 mt-2 bg-gray-50 rounded-lg">
                                            +{expensesData.length - 6} more transactions
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="text-center text-gray-500 py-6 text-sm bg-gray-50 rounded-lg">
                                    <div className="w-12 h-12 bg-gray-200 rounded-full mx-auto mb-2 flex items-center justify-center">
                                        💰
                                    </div>
                                    No financial data available
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Devices */}
                    <div className="bg-white rounded-xl shadow-md border border-gray-200/50 overflow-hidden">
                        <div className="p-3 border-b bg-gradient-to-r from-purple-50 to-violet-50">
                            <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                                <span className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center text-sm">📱</span>
                                Your Devices
                                <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full shadow-sm">
                                    {registeredDevices} Registered
                                </span>
                            </h2>
                        </div>
                        <div className="p-3">
                            <div className="grid grid-cols-2 gap-2">
                                {devicesData.slice(0, 6).map((device) => (
                                    <div key={device.id} className="border border-gray-200 rounded-lg p-2 hover:shadow-sm transition-all duration-200 bg-gradient-to-br from-white to-gray-50">
                                        <div className="flex flex-col gap-2">
                                            <div className="flex items-center gap-2">
                                                <div className="flex-shrink-0 w-7 h-7 bg-gray-100 rounded-lg overflow-hidden">
                                                    {device.device_image && (
                                                        <img
                                                            src={device.device_image}
                                                            alt={device.device_type}
                                                            className="w-full h-full object-contain"
                                                        />
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="text-xs font-medium truncate">{device.device_type}</h3>
                                                    <p className="text-xs text-gray-500 truncate">{device.device_id}</p>
                                                </div>
                                            </div>
                                            <div className="flex justify-between items-center gap-1">
                                                <span className={`text-xs px-2 py-1 rounded-full flex-1 text-center ${device.is_registered
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {device.is_registered ? 'Active' : 'Inactive'}
                                                </span>
                                                {device.motor_status && (
                                                    <span className={`text-xs px-2 py-1 rounded-full ${device.motor_status === 'ON'
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                        }`}>
                                                        {device.motor_status}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {devicesData.length > 6 && (
                                <div className="text-center text-xs text-gray-500 py-2 mt-2 bg-gray-50 rounded-lg">
                                    +{devicesData.length - 6} more devices
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Activity Summary */}
                    <div className="bg-white rounded-xl shadow-md border border-gray-200/50 overflow-hidden">
                        <div className="p-3 border-b bg-gradient-to-r from-orange-50 to-amber-50">
                            <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                                <span className="w-6 h-6 bg-orange-100 rounded-lg flex items-center justify-center text-sm">📊</span>
                                Activity Summary
                            </h2>
                        </div>
                        <div className="p-3">
                            {/* Forum Stats */}
                            <div className="grid grid-cols-3 gap-2 text-center mb-3">
                                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-2.5 rounded-lg border border-orange-200 shadow-sm">
                                    <div className="text-lg font-bold text-orange-800">{forumStats.no_of_questions || 0}</div>
                                    <div className="text-xs text-orange-600">Questions</div>
                                </div>
                                <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-2.5 rounded-lg border border-amber-200 shadow-sm">
                                    <div className="text-lg font-bold text-amber-800">{forumStats.total_replies || 0}</div>
                                    <div className="text-xs text-amber-600">Replies</div>
                                </div>
                                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-2.5 rounded-lg border border-yellow-200 shadow-sm">
                                    <div className="text-lg font-bold text-yellow-800">{forumStats.total_posts_posted || 0}</div>
                                    <div className="text-xs text-yellow-600">Posts</div>
                                </div>
                            </div>

                            {/* Recent Points */}
                            <div className="mb-3">
                                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                                    <span className="text-green-500">🏆</span> Recent Points History
                                </h3>
                                <div className="space-y-1.5">
                                    {pointsData.length > 0 ? (
                                        pointsData.map((point, index) => {
                                            const isEarned = point.type === "earned";
                                            const isUsed = point.type === "used";
                                            const isRefunded = point.type === "refunded";
                                            const bgColor = isEarned ? "bg-green-50" : isUsed ? "bg-red-50" : "bg-blue-50";
                                            const textColor = isEarned ? "text-green-700" : isUsed ? "text-red-700" : "text-blue-700";
                                            const badgeColor = isEarned ? "bg-green-100" : isUsed ? "bg-red-100" : "bg-blue-100";
                                            const sign = isEarned ? "+" : isUsed ? "-" : "↩";

                                            return (
                                                <div key={point.id || index} className={`flex justify-between items-center text-sm ${bgColor} p-2 rounded-lg border border-gray-200`}>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`text-xs font-medium ${textColor} px-2 py-0.5 rounded-full ${badgeColor}`}>
                                                                {point.type?.toUpperCase() || "UNKNOWN"}
                                                            </span>
                                                            {point.order_id && (
                                                                <span className="text-xs text-gray-500 truncate" title={point.order_id}>
                                                                    Order: {point.order_id.substring(0, 8)}...
                                                                </span>
                                                            )}
                                                        </div>
                                                        {point.reason && (
                                                            <div className="text-xs text-gray-600 mt-1 truncate" title={point.reason}>
                                                                {point.reason}
                                                            </div>
                                                        )}
                                                        {point["date assigned"] && (
                                                            <div className="text-xs text-gray-500 mt-0.5">
                                                                {new Date(point["date assigned"]).toLocaleDateString()}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <span className={`font-medium ${textColor} ${badgeColor} px-2 py-0.5 rounded-full ml-2 whitespace-nowrap`}>
                                                        {sign} {point.points} (₹{point.points / 10})
                                                    </span>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="text-center text-gray-500 py-3 text-xs bg-gray-50 rounded-lg">
                                            No points history available
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Order History */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200/50 overflow-hidden">
                <div className="p-4 border-b bg-gradient-to-r from-indigo-50 to-blue-50">
                    <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                        <span className="w-6 h-6 bg-indigo-100 rounded-lg flex items-center justify-center text-sm">📋</span>
                        Order History
                    </h2>
                </div>
                <div className="p-4">
                    <UserOrderHistory user_id={item.id} showTopItems={false} />
                </div>
            </div>

            {/* Modals */}
            <ShowModal
                isOpen={showExpenseAdd}
                title={"Add Expense"}
                componentToPass={<ExpenseForm
                    item={item}
                    onSave={handleExpenseSave}
                />}
                modalWidth={"35%"}
                onRequestClose={() => closeExpenseModal()}
            />
            <ShowModal
                isOpen={showMandiAdd}
                title={"Add Farming Commodity"}
                modalWidth={"35%"}
                componentToPass={<CommodityForm
                    item={item}
                    onSave={closeMandiModal}
                />}
                onRequestClose={() => closeMandiModal()}
            />

            {/* Expense Detailed View Modal */}
            <ExpenseDetailedView
                expense={selectedExpense}
                isOpen={isExpenseDetailOpen}
                onClose={handleCloseExpenseDetail}
            />
        </div>
    );
}

// Helper Components
function StatCard({ value, label, icon, color }) {
    return (
        <div className="bg-white rounded-xl shadow-md border border-gray-200/50 p-3 hover:shadow-lg transition-all duration-200 transform hover:scale-105">
            <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color} border shadow-sm`}>
                    <span className="text-lg">{icon}</span>
                </div>
                <div className="flex-1">
                    <div className="font-bold text-gray-800 text-lg leading-tight">{value}</div>
                    <div className="text-xs text-gray-600 font-medium">{label}</div>
                </div>
            </div>
        </div>
    );
}

function HealthIndicator({ label, status }) {
    const statusColors = {
        'Better': 'bg-green-100 text-green-800 border-green-200 shadow-sm',
        'Good': 'bg-blue-100 text-blue-800 border-blue-200 shadow-sm',
        'Poor': 'bg-yellow-100 text-yellow-800 border-yellow-200 shadow-sm',
        'Dead/Stressed': 'bg-red-100 text-red-800 border-red-200 shadow-sm'
    };

    return (
        <div className={`text-center py-1.5 px-1 rounded-lg text-xs border ${statusColors[status] || 'bg-gray-100 text-gray-800 border-gray-200 shadow-sm'}`}>
            <div className="font-semibold text-xs leading-tight">{label}</div>
            <div className="text-xs opacity-90">{status}</div>
        </div>
    );
}

export default UserDashboardView;